import React, { useState, useEffect, useRef } from "react";
import { signInWithCustomToken, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore'; // Added for Firestore interaction in frontend
import { db } from '../../config/firebase'; // Added for Firestore interaction in frontendw

// Production Firebase Functions URLs
const BANKID_API_URL = "https://us-central1-hyria-58020.cloudfunctions.net/bankidSignProxy";
const BANKID_COLLECT_URL = "https://us-central1-hyria-58020.cloudfunctions.net/bankidCollectProxy";
const BANKID_AUTH_COMPLETE_URL = "https://us-central1-hyria-58020.cloudfunctions.net/bankidAuthComplete";
const PERSONDATA_URL = "https://us-central1-hyria-58020.cloudfunctions.net/getPersonData";
const BANKID_QR_REFRESH_URL = "https://us-central1-hyria-58020.cloudfunctions.net/bankidQrRefresh";

const BankIDAuthTest = () => {
  const navigate = useNavigate();
  const [ip, setIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [bankidResponse, setBankidResponse] = useState(null);
  const [bankidLoading, setBankidLoading] = useState(false);
  const [bankidError, setBankidError] = useState("");
  const [pollingStatus, setPollingStatus] = useState("");
  const [pollingResult, setPollingResult] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [personData, setPersonData] = useState(null);
  const [personDataLoading, setPersonDataLoading] = useState(false);
  
  // QR refresh states
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [originalQrUrl, setOriginalQrUrl] = useState('');
  const [qrRefreshing, setQrRefreshing] = useState(false);
  const [qrLastRefresh, setQrLastRefresh] = useState(null);
  
  // Refs
  const pollingIntervalRef = useRef(null);
  const qrRefreshIntervalRef = useRef(null);

  // Skapa BankID-länk från AutoStartToken
  const createBankIDLink = (autoStartToken) => {
    return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${encodeURIComponent(window.location.origin)}`;
  };

  // QR refresh function
  const refreshQRCode = async () => {
    if (!originalQrUrl) return;
    
    try {
      setQrRefreshing(true);

      
      const response = await fetch(BANKID_QR_REFRESH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalQrUrl })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      
      if (data.success && data.qrImageUrl) {
        setQrImageUrl(data.qrImageUrl);
        setQrLastRefresh(new Date());

      } else {
      }
      
    } catch (error) {
      if (qrRefreshIntervalRef.current) {
        clearInterval(qrRefreshIntervalRef.current);

      }
    } finally {
      setQrRefreshing(false);
    }
  };

  // Funktion för att hantera lyckad BankID-autentisering
  const handleSuccessfulAuth = async (completionData) => {
    try {

      
      // Anropa bankidAuthComplete för att spara användare och få custom token
      const response = await fetch(BANKID_AUTH_COMPLETE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: completionData.user,
          device: completionData.device
        })
      });
      
      const data = await response.json();

      
      if (data.success) {
        // Hämta PersonData baserat på personnummer
        await fetchPersonData(completionData.user.personalNumber);
        
        // Logga in användaren med custom token eller skapa användare
        await loginUserWithCustomToken(data.customToken, data.uid);
      } else {
        setError("Kunde inte skapa användare: " + (data.error || "Okänt fel"));
      }
      
    } catch (error) {
      setError("Kunde inte spara användare: " + error.message);
    }
  };

  // Funktion för att hämta PersonData
  const fetchPersonData = async (personalNumber) => {
    try {
      setPersonDataLoading(true);

      
      // Kontrollera först om användaren redan har en verifierad adress
      const { checkUserHasVerifiedAddress } = await import('../../api/housing');
      const verifiedAddress = await checkUserHasVerifiedAddress(auth.currentUser?.uid || '');
      
      if (verifiedAddress.hasVerifiedAddress) {

        setPersonDataLoading(false);
        return; // Hoppa över PersonData-anrop
      }
      
      const response = await fetch(PERSONDATA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ssn: personalNumber })
      });
      
      const data = await response.json();

      
      if (data.success) {
        setPersonData(data.data);

        
        // Spara PersonData temporärt för att visa pop-up senare på startsidan
        if (data.data.Basic?.Street) {
          // Spara i sessionStorage för att använda efter navigering
          sessionStorage.setItem('pendingPersonData', JSON.stringify(data.data));

        }
      } else {
        
        // För testning: Lägg till mock PersonData om riktig API misslyckas
        if (process.env.NODE_ENV === 'development') {

          const mockPersonData = {
            Basic: {
              Street: "Storgatan 123",
              ZipCode: "12345",
              City: "Stockholm",
              ApartmentNumber: "1001",
              Entrance: "A"
            }
          };
          
          setPersonData(mockPersonData);

          
          // Spara mock-data temporärt
          sessionStorage.setItem('pendingPersonData', JSON.stringify(mockPersonData));

        }
        
        // Vi fortsätter ändå - PersonData är inte kritiskt
      }
      
    } catch (error) {
      // Vi fortsätter ändå - PersonData är inte kritiskt
    } finally {
      setPersonDataLoading(false);
    }
  };

  // Funktion för att logga in användaren med custom token
  const loginUserWithCustomToken = async (customToken, uid) => {
    try {
      setAuthLoading(true);

      
      const userCredential = await signInWithCustomToken(auth, customToken);

      navigate('/');
      
    } catch (error) {
      setError("Kunde inte logga in användare: " + error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Fallback login för development mode (när ingen custom token finns)
  const loginUserWithDevelopmentMode = async (uid, bankidUserData) => {
    try {
      setAuthLoading(true);

      
      const personalNumber = uid.replace('bankid_', '');
      
      // Kontrollera om användare redan finns i Firestore
      const usersCollection = collection(db, 'users');
      const q = query(usersCollection, where("personalNumber", "==", personalNumber));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Befintlig användare hittad
        const existingUserDoc = querySnapshot.docs[0];

        
        try {
          const email = `bankid_${personalNumber}@bankid.local`;
          const password = 'bankid123';
          const userCredential = await signInWithEmailAndPassword(auth, email, password);

          
          // Uppdatera lastLogin
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            lastLogin: new Date().toISOString(),
            bankidVerified: true
          }, { merge: true });
          
        } catch (authError) {
          setError("Tekniskt fel vid inloggning. Försök igen.");
          return;
        }
        
      } else {
        // Skapa ny användare

        
        const email = `bankid_${personalNumber}@bankid.local`;
        const password = 'bankid123';
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        
        const userData = {
          personalNumber: personalNumber,
          firstName: bankidUserData?.givenName || 'BankID',
          lastName: bankidUserData?.surname || 'User',
          fullName: bankidUserData?.name || 'BankID User',
          bankidVerified: true,
          lastLogin: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          ipAddress: '127.0.0.1',
          role: 'user',
          emailVerified: false
        };
        
        await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      }
      
      navigate('/');
      
    } catch (error) {
      setError("Kunde inte logga in användare: " + error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  // Funktion för att hämta status från BankID
  const pollStatus = async (orderRef) => {
    try {

      const response = await fetch(BANKID_COLLECT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderRef })
      });
      
      const data = await response.json();

      
      if (data?.apiCallResponse?.Success) {
        const status = data.apiCallResponse.Response?.Status;
        const hintCode = data.apiCallResponse.Response?.HintCode;
        

        
        if (status === "complete") {
          // Autentisering klar

          setPollingStatus("complete");
          setPollingResult(data.apiCallResponse.Response?.CompletionData);
          setIsPolling(false);
          
          // Stoppa alla intervaller
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          if (qrRefreshIntervalRef.current) {
            clearInterval(qrRefreshIntervalRef.current);
          }
          
          // Hantera lyckad autentisering
          await handleSuccessfulAuth(data.apiCallResponse.Response?.CompletionData);
          
        } else if (status === "failed") {
          // Autentisering misslyckades

          setPollingStatus("failed");
          setPollingResult(data.apiCallResponse.Response);
          setIsPolling(false);
          
          // Stoppa alla intervaller
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          if (qrRefreshIntervalRef.current) {
            clearInterval(qrRefreshIntervalRef.current);
          }
        } else if (status === "pending") {
          // Fortsätt polling

          // Visa hint code för användaren
          const hintMessages = {
            "outstandingTransaction": "Väntar på att du ska identifiera dig i BankID-appen...",
            "noClient": "Starta BankID-appen på din telefon",
            "started": "BankID-appen har startats, identifiera dig...",
            "userSign": "Du har identifierat dig, väntar på att signera..."
          };
          const statusMessage = hintMessages[hintCode] || "Väntar på BankID-autentisering...";
          setPollingStatus(statusMessage);
        } else {

          setPollingStatus(`Okänd status: ${status}`);
        }
      } else {
        setPollingStatus("Fel vid polling: " + (data?.authResponse?.ErrorMessage || "Okänt fel"));
      }
    } catch (err) {
      setPollingStatus("Nätverksfel vid polling");
    }
  };

  // Starta polling och QR refresh när vi får OrderRef
  useEffect(() => {
    const bankidData = bankidResponse?.apiCallResponse?.Response;


    
    if (bankidData?.OrderRef && !isPolling && !pollingResult) {

      setIsPolling(true);
      setPollingStatus("Väntar på BankID-autentisering...");
      
      // Starta polling var 2:e sekund
      pollingIntervalRef.current = setInterval(() => {

        pollStatus(bankidData.OrderRef);
      }, 2000);
      
      // Start QR refresh var 5:e sekund (endast för desktop)
      if (deviceType !== "mobile") {
        qrRefreshIntervalRef.current = setInterval(() => {

          refreshQRCode();
        }, 5000);
      }
      
      // Gör första anropet direkt
      pollStatus(bankidData.OrderRef);
    }
    
    // Cleanup när komponenten unmountas
    return () => {
      if (pollingIntervalRef.current) {

        clearInterval(pollingIntervalRef.current);
      }
      if (qrRefreshIntervalRef.current) {

        clearInterval(qrRefreshIntervalRef.current);
      }
    };
  }, [bankidResponse]); // Ta bort isPolling och pollingResult från dependencies

  const handleGetIp = async () => {
    setLoading(true);
    setError("");
    setBankidResponse(null);
    setBankidError("");
    setPollingStatus("");
    setPollingResult(null);
    setIsPolling(false);
    setAuthLoading(false);
    setPersonData(null);
    setPersonDataLoading(false);
    
    // Reset QR states
    setQrImageUrl('');
    setOriginalQrUrl('');
    setQrRefreshing(false);
    setQrLastRefresh(null);
    
    // Stoppa eventuell pågående polling och QR refresh
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (qrRefreshIntervalRef.current) {
      clearInterval(qrRefreshIntervalRef.current);
    }
    
    try {
      // Anropa BankID sign-endpoint via vår proxy
      setBankidLoading(true);
      const signRes = await fetch(BANKID_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}) // Tom payload, backend hanterar allt
      });
      const signData = await signRes.json();
      setBankidResponse(signData);
      setBankidLoading(false);

      
      // Sätt IP och device type från backend-svaret
      if (signData.clientIP) {
        setIp(signData.clientIP);

      }
      
      if (signData.deviceType) {
        setDeviceType(signData.deviceType);

      }
      
      // Set initial QR code URL
      if (signData?.apiCallResponse?.Response?.QrImage) {
        const initialQrUrl = signData.apiCallResponse.Response.QrImage;
        setOriginalQrUrl(initialQrUrl);
        setQrImageUrl(initialQrUrl);
        setQrLastRefresh(new Date());

      }
      
      if (!signData?.authResponse?.Success || !signData?.apiCallResponse?.Success) {
        setBankidError(signData?.authResponse?.ErrorMessage || signData?.apiCallResponse?.StatusMessage || "BankID-förfrågan misslyckades");
      }
    } catch (err) {
      setError("Kunde inte anropa BankID-tjänsten");
      setBankidLoading(false);
      setBankidError("BankID-förfrågan misslyckades");
    } finally {
      setLoading(false);
    }
  };

  // Hämta BankID-data från response
  const bankidData = bankidResponse?.apiCallResponse?.Response;
  const bankidLink = bankidData?.AutoStartToken ? createBankIDLink(bankidData.AutoStartToken) : null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background">
      <div className="bg-white dark:bg-zinc-900 shadow-lg rounded-2xl p-8 w-full max-w-md flex flex-col items-center">
        <h1 className="text-3xl font-bold mb-6 text-center">BankID-autentiseringstest</h1>
        <p className="mb-8 text-center text-muted-foreground">
          Detta är en fristående testsida för BankID-autentisering.
        </p>
        <button
          className="px-6 py-3 rounded-xl bg-primary text-white font-semibold shadow-md hover:bg-primary/90 transition mb-4"
          onClick={handleGetIp}
          disabled={loading || bankidLoading}
        >
          {loading || bankidLoading ? "Bearbetar..." : "Logga in med BankID"}
        </button>
        {ip && (
          <div className="mt-2 text-center text-sm text-green-600 dark:text-green-400">
            Din publika IP-adress: <span className="font-mono">{ip}</span>
          </div>
        )}
        {deviceType && (
          <div className="mt-2 text-center text-sm text-blue-600 dark:text-blue-400">
            Upptäckt enhetstyp: <span className="font-mono">{deviceType}</span>
          </div>
        )}
        {bankidLoading && (
          <div className="mt-4 text-center text-sm text-muted-foreground animate-pulse">
            Skickar BankID-förfrågan...
          </div>
        )}
        {bankidError && (
          <div className="mt-2 text-center text-sm text-red-600 dark:text-red-400">
            {bankidError}
          </div>
        )}
        
        {/* Visa polling status */}
        {isPolling && pollingStatus && (
          <div className="mt-4 text-center">
            <div className="text-sm text-blue-600 dark:text-blue-400 animate-pulse">
              {pollingStatus}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Pollar status var 2:e sekund...
            </div>
          </div>
        )}
        
        {/* Visa resultat när complete */}
        {pollingResult && pollingStatus === "complete" && (
          <div className="mt-4 w-full bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
            <div className="text-center mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                Autentisering lyckades!
              </h3>
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">
              <div><b>Namn:</b> {pollingResult.user?.name}</div>
              <div><b>Personnummer:</b> {pollingResult.user?.personalNumber}</div>
              <div><b>IP-adress:</b> {pollingResult.device?.ipAddress}</div>
            </div>
            
            {/* Visa PersonData loading */}
            {personDataLoading && (
              <div className="mt-3 text-center">
                <div className="text-sm text-green-600 dark:text-green-400 animate-pulse">
                  Hämtar adressdata...
                </div>
              </div>
            )}
            
            {/* Visa PersonData resultat */}
            {personData && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  Adressdata från SPAR:
                </h4>
                <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  <div><b>Gatuadress:</b> {personData.Basic?.Street}</div>
                  <div><b>Postnummer:</b> {personData.Basic?.ZipCode}</div>
                  <div><b>Ort:</b> {personData.Basic?.City}</div>
                  {personData.Basic?.ApartmentNumber && (
                    <div><b>Lägenhetsnummer:</b> {personData.Basic.ApartmentNumber}</div>
                  )}
                  {personData.Basic?.Entrance && (
                    <div><b>Entré:</b> {personData.Basic.Entrance}</div>
                  )}
                </div>
              </div>
            )}
            
            {authLoading && (
              <div className="mt-3 text-center">
                <div className="text-sm text-green-600 dark:text-green-400 animate-pulse">
                  Sparar användare och loggar in...
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Visa fel när failed */}
        {pollingResult && pollingStatus === "failed" && (
          <div className="mt-4 w-full bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
            <div className="text-center mb-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                Autentisering misslyckades
              </h3>
            </div>
            <div className="text-sm text-red-700 dark:text-red-300">
              <div><b>Anledning:</b> {pollingResult.hintCode}</div>
            </div>
          </div>
        )}
        
        {/* Visa QR-kod eller länk beroende på device */}
        {bankidData && !bankidError && !pollingResult && (
          <div className="mt-6 w-full">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-200 mb-2">
                {deviceType === "mobile" ? "Öppna BankID-appen" : "Scanna QR-koden"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {deviceType === "mobile" 
                  ? "Klicka på länken nedan för att öppna BankID-appen direkt"
                  : "Öppna BankID-appen på din telefon och scanna QR-koden"
                }
              </p>
            </div>
            
            {deviceType === "mobile" ? (
              // Mobil: Visa direktlänk
              <div className="text-center">
                <a
                  href={bankidLink}
                  className="inline-block px-6 py-3 bg-green-600 text-white font-semibold rounded-xl shadow-md hover:bg-green-700 transition mb-4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Öppna BankID-appen
                </a>
                <p className="text-xs text-muted-foreground">
                  Länk: <span className="font-mono break-all">{bankidLink}</span>
                </p>
              </div>
            ) : (
              // Desktop/iPad: Visa QR-kod från BankID-API
              <div className="text-center">
                {(qrImageUrl || bankidData.QrImage) && (
                  <div className="bg-white p-4 rounded-xl shadow-md inline-block mb-4 relative">
                    <img 
                      src={qrImageUrl || bankidData.QrImage} 
                      alt="BankID QR-kod" 
                      className="w-48 h-48"
                    />
                    {qrRefreshing && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
                        <div className="flex items-center space-x-2 text-blue-600">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm font-medium">Uppdaterar...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>QR-kod från BankID-API</p>
                  {qrLastRefresh && (
                    <p className="flex items-center justify-center space-x-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      <span>Uppdaterad: {qrLastRefresh.toLocaleTimeString()}</span>
                    </p>
                  )}
                  {deviceType !== "mobile" && (
                    <p className="text-green-600 font-medium">
                      ✓ QR-koden uppdateras automatiskt var 5:e sekund
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Visa BankID-data för debug */}
            <div className="mt-4 p-3 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-xs">
              <div><b>OrderRef:</b> {bankidData.OrderRef}</div>
              <div><b>AutoStartToken:</b> {bankidData.AutoStartToken}</div>
              {bankidData.QrImage && (
                <div>
                  <b>QrImage:</b> <a href={bankidData.QrImage} target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Visa QR från API</a>
                </div>
              )}
            </div>
          </div>
        )}
        
        {error && (
          <div className="mt-2 text-center text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* Adress-bekräftelse Modal */}
      {/* Removed as per edit hint */}
    </div>
  );
};

export default BankIDAuthTest; 